import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCompetition, useCompetitionContext, useRequestToJoinCompetition, useCompetitionIdParam } from '@/hooks/useCompetitions';
import { useCategories } from '@/hooks/useCategories';
import { useRejectSubmission, useSubmissionProgress, useSubmissions } from '@/hooks/useSubmissions';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { InfoHint } from '@/components/ui/Tooltip';
import { SubmissionUploadForm } from '@/components/submissions/SubmissionUploadForm';
import { PaymentCard } from '@/components/payments/PaymentCard';
import { useRanking } from '@/hooks/useJudging';
import { SubmissionCard } from '@/components/submissions/SubmissionCard';
import { RejectSubmissionModal } from '@/components/submissions/RejectSubmissionModal';
import { CompetitionStatus, SubmissionStatus, type Submission } from '@/api/types';
import { competitionNavItems } from '@/lib/navigation';
import { isSuperAdmin } from '@/lib/roles';
import { useAuthStore } from '@/store/authStore';
import { ArrowRight } from 'lucide-react';
import { competitionStatusLabel, competitionStatusTone, FZS_PATRONAGE_LABEL, COMPETITION_LIFECYCLE_ACTIONS } from '@/lib/competitions';
import { CompetitionLifecycleActions } from '@/components/competitions/CompetitionLifecycleActions';
import { CompetitionWorkspaceTabs } from '@/components/organizer/CompetitionWorkspace';

type CompetitionView = 'staff' | 'competitor';

/** Lower-case and strip diacritics so "sagra" finds "Šagra". */
function normalizeSearch(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

export function CompetitionDetailsPage() {
  const { competitionId } = useCompetitionIdParam();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined);
  const [onlyPending, setOnlyPending] = useState(false);
  // After the selection for the exhibition exists, the entries can be narrowed to the selected ones.
  const [onlySelected, setOnlySelected] = useState(false);
  const [rejecting, setRejecting] = useState<{ id: string; title: string } | null>(null);

  const competition = useCompetition(competitionId);
  const categories = useCategories(competitionId);
  // Scoped to THIS competition specifically - a user who judges this one but
  // competes in a different one shouldn't see the "submit an entry" widget
  // here just because they hold the Competitor role somewhere on the platform.
  const context = useCompetitionContext(competitionId);
  const canManageHere = context.data?.canManage ?? false;
  const isJudgeHere = context.data?.isJudge ?? false;
  const isParticipantHere = context.data?.isParticipant ?? false;
  const hasStaffView = canManageHere || isJudgeHere;
  // Everyone else (not signed up, request pending or rejected) only learns that the competition exists - no content.
  const hasContentAccess = canManageHere || isJudgeHere || isParticipantHere;
  const requestToJoin = useRequestToJoinCompetition();
  const admin = isSuperAdmin(useAuthStore((s) => s.user));
  const hasCompetitorView = isParticipantHere;
  // Someone who both manages/judges AND has their own entry in this same
  // competition gets an explicit switch instead of both views blended
  // together - that blending is exactly what looked like "seeing the
  // competitor view while judging".
  const hasBothViews = hasStaffView && hasCompetitorView;
  const staffViewLabel = canManageHere ? 'Organizator' : 'Sodnik';

  const [search, setSearch] = useState('');
  useEffect(() => setSearch(''), [competitionId]);

  // After judging the staff can order the entries by their average score. The ranking endpoint only
  // answers once the competition is Completed, so it is not even requested before that.
  const [sortByScore, setSortByScore] = useState(false);
  useEffect(() => setSortByScore(false), [competitionId]);

  const [selectedView, setSelectedView] = useState<CompetitionView>('staff');
  useEffect(() => setSelectedView('staff'), [competitionId]);

  const viewingAsCompetitor = hasCompetitorView && (!hasStaffView || selectedView === 'competitor');
  const viewingAsStaff = hasStaffView && (!hasCompetitorView || selectedView === 'staff');
  const canModerate = viewingAsStaff && canManageHere;
  const scoresAvailable = viewingAsStaff && (competition.data?.status ?? 0) >= CompetitionStatus.Awarding;
  // Staff see the selection for the exhibition as soon as it is made; competitors once the results are published.
  const rankingWanted =
    scoresAvailable || (viewingAsCompetitor && (competition.data?.status ?? 0) >= CompetitionStatus.Completed);
  const ranking = useRanking(rankingWanted ? competitionId : undefined);
  const averageBySubmission = new Map((ranking.data?.items ?? []).map((r) => [r.submissionId, r.averageScore]));
  const selectedIds = new Set((ranking.data?.items ?? []).filter((r) => r.isSelected).map((r) => r.submissionId));
  const selectionVisible = Boolean(competition.data?.exhibitionSelection) && selectedIds.size > 0;
  const rejectSubmission = useRejectSubmission();

  // GET /submissions only accepts exactly one of categoryId/competitionId/
  // mine=true (no competitorId filter, and mine ignores category/competition
  // scoping server-side), so a plain competitor gets "mine" and we narrow to
  // this competition/category client-side; staff get the server-side
  // competition or category filter directly.
  const onlyMine = viewingAsCompetitor;
  const submissions = useSubmissions(
    onlyMine ? { mine: true } : { competitionId, categoryId: selectedCategoryId },
  );
  // Only fetched for competitors (the endpoint is Competitor-role-only) -
  // drives both the "X of Y uploaded" progress display and which categories
  // are still open to submit into below.
  const submissionProgress = useSubmissionProgress(hasCompetitorView ? competitionId : undefined);
  const categoriesStillOpen = new Set(
    submissionProgress.data?.categories.filter((c) => c.remaining > 0).map((c) => c.categoryId) ?? [],
  );
  // Falls back to "all categories" while progress hasn't loaded yet, so the
  // form doesn't flash empty and then populate a moment later.
  const uploadableCategories = submissionProgress.isSuccess
    ? (categories.data?.items ?? []).filter((c) => categoriesStillOpen.has(c.id))
    : (categories.data?.items ?? []);
  const searchTerm = normalizeSearch(search);
  const visibleSubmissions: Submission[] =
    submissions.data?.items.filter((s) => {
      if (!onlyMine) return true;
      if (s.competitionId !== competitionId) return false;
      if (selectedCategoryId && s.categoryId !== selectedCategoryId) return false;
      return true;
    }).filter((s) => !(canModerate && onlyPending) || s.status === SubmissionStatus.Submitted)
      .filter((s) => !(selectionVisible && onlySelected) || selectedIds.has(s.id))
      .filter((s) => !searchTerm || normalizeSearch(s.title).includes(searchTerm)) ?? [];
  if (scoresAvailable && sortByScore) {
    // Best first; entries nobody scored go last, ties by title.
    visibleSubmissions.sort(
      (a, b) =>
        (averageBySubmission.get(b.id) ?? -1) - (averageBySubmission.get(a.id) ?? -1) || a.title.localeCompare(b.title, 'sl'),
    );
  }

  // Entries are shown under the category they belong to. Ordered by score they stay one flat list (the order is the point).
  const categoryNames = new Map((categories.data?.items ?? []).map((c) => [c.id, c.name]));
  const categoryOrder = (categories.data?.items ?? []).map((c) => c.id);
  const submissionGroups: { id: string; name: string | null; items: Submission[] }[] =
    scoresAvailable && sortByScore
      ? [{ id: 'all', name: null, items: visibleSubmissions }]
      : Array.from(new Set(visibleSubmissions.map((s) => s.categoryId)))
          .sort((a, b) => {
            const ia = categoryOrder.indexOf(a);
            const ib = categoryOrder.indexOf(b);
            return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
          })
          .map((id) => ({
            id,
            name: categoryNames.get(id) ?? 'Brez kategorije',
            items: visibleSubmissions.filter((s) => s.categoryId === id),
          }));

  // Review summary for organizers: how many entries still need a decision
  // before judging can start (counted over the whole competition, ignoring
  // the category/pending filters).
  const allSubmissions = submissions.data?.items ?? [];
  const pendingCount = allSubmissions.filter((s) => s.status === SubmissionStatus.Submitted).length;
  const acceptedCount = allSubmissions.filter((s) => s.status === SubmissionStatus.Accepted).length;
  const rejectedCount = allSubmissions.filter((s) => s.status === SubmissionStatus.Rejected).length;

  if (competition.isLoading || context.isLoading) return <PageSpinner />;
  if (competition.isError) {
    return <ErrorState message={getApiErrorMessage(competition.error)} onRetry={() => competition.refetch()} />;
  }
  if (!competition.data) return <EmptyState title="Tekmovanje ni najdeno" />;

  const comp = competition.data;
  // Entries can be rejected while submissions run and during the review; judging has its own, locked list.
  const reviewOpen = comp.status === CompetitionStatus.Active || comp.status === CompetitionStatus.Review;
  // The event's own logo takes priority; fall back to its organization's logo.
  const displayLogoUrl = comp.logoUrl ?? comp.organizationLogoUrl;
  // Judge and competitor shortcuts; the organizer's per-competition pages are the tabs instead.
  const quickLinks = competitionNavItems({
    competitionId,
    slug: comp.slug,
    status: comp.status,
    canManage: false,
    isJudge: isJudgeHere,
    isParticipant: isParticipantHere,
  }).filter((item) => item.group);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="mb-1 flex items-center gap-3">
          {displayLogoUrl && (
            <img src={displayLogoUrl} alt={`${comp.name} logo`} className="h-10 w-10 rounded-photo object-contain" />
          )}
          <h1 className="text-xl font-semibold text-fg">{comp.name}</h1>
          <Badge tone={competitionStatusTone(comp.status)}>{competitionStatusLabel(comp.status)}</Badge>
          {comp.isFzsPatronage && <Badge tone="Fzs">{FZS_PATRONAGE_LABEL}</Badge>}
        </div>
        {comp.organizationName && <p className="text-xs text-fg-muted">Organizator: {comp.organizationName}</p>}
        {comp.description && <p className="text-sm text-fg-secondary">{comp.description}</p>}
        <Link to={`/razpis/${comp.slug}`} className="mt-1 inline-block text-sm text-accent-text hover:underline">
          Razpis in pravila tekmovanja
        </Link>
        <div className="mt-2 flex flex-wrap gap-4 text-xs text-fg-muted">
          {comp.startDate && <span>Začetek: {new Date(comp.startDate).toLocaleDateString()}</span>}
          {comp.endDate && <span>Konec: {new Date(comp.endDate).toLocaleDateString()}</span>}
          {comp.submissionDeadline && (
            <span>Rok za oddajo: {new Date(comp.submissionDeadline).toLocaleString()}</span>
          )}
        </div>
      </div>

      {!hasContentAccess ? (
        <Card>
          <CardBody className="flex flex-col items-start gap-3">
            <h2 className="font-semibold text-fg">
              {comp.isOpenForSignup ? 'Prijavite se na tekmovanje' : 'Vsebina tekmovanja ni javna'}
            </h2>
            <p className="text-sm text-fg-secondary">
              {comp.isOpenForSignup
                ? 'Z enim klikom ste prijavljeni in lahko takoj oddate prispevke.'
                : 'Kategorije, prispevke in rezultate vidijo samo prijavljeni tekmovalci, sodniki in organizatorji.'}
            </p>
            {comp.isOpenForSignup && (
              <>
                {requestToJoin.isError && <p className="text-sm text-danger">{getApiErrorMessage(requestToJoin.error)}</p>}
                <Button isLoading={requestToJoin.isPending} onClick={() => requestToJoin.mutate(competitionId)}>
                  Prijavi se na tekmovanje
                </Button>
              </>
            )}
          </CardBody>
        </Card>
      ) : (
      <>
      {(canManageHere || admin) && <CompetitionWorkspaceTabs competitionId={competitionId} />}
      {quickLinks.length > 0 && (
      <nav aria-label="Moje možnosti v tekmovanju" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {quickLinks.map((item) => (
            <Link
              key={`${item.group}-${item.to}`}
              to={item.to}
              className="group flex items-center justify-between rounded-card border border-line bg-surface-1 p-4 shadow-card transition-colors duration-fast hover:border-line-strong hover:bg-surface-2"
            >
              <span>
                <span className="block font-mono text-[10px] uppercase tracking-[0.08em] text-fg-muted">{item.group}</span>
                <span className="mt-0.5 block text-sm font-semibold text-fg">{item.label}</span>
              </span>
              <ArrowRight size={16} aria-hidden="true" className="text-fg-muted transition-transform duration-fast group-hover:translate-x-1 group-hover:text-fg" />
            </Link>
          ))}
      </nav>
      )}
      {hasBothViews && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-fg-muted">Ogled kot:</span>
          <div className="inline-flex rounded-control border border-line bg-surface-1 p-[3px]">
            <button
              onClick={() => setSelectedView('staff')}
              className={`rounded-[4px] px-3 py-1 text-xs font-medium transition-colors duration-fast ${
                selectedView === 'staff' ? 'bg-surface-3 text-fg' : 'text-fg-secondary hover:text-fg'
              }`}
            >
              {staffViewLabel}
            </button>
            <button
              onClick={() => setSelectedView('competitor')}
              className={`rounded-[4px] px-3 py-1 text-xs font-medium transition-colors duration-fast ${
                selectedView === 'competitor'
                  ? 'bg-surface-3 text-fg'
                  : 'text-fg-secondary hover:text-fg'
              }`}
            >
              Tekmovalec
            </button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-fg">Kategorije</h2>
        </CardHeader>
        <CardBody>
          {categories.isLoading && <PageSpinner />}
          {categories.isError && <ErrorState message={getApiErrorMessage(categories.error)} />}
          {categories.isSuccess && categories.data.items.length === 0 && (
            <p className="text-sm text-fg-muted">Temu tekmovanju še ni bila dodana nobena kategorija.</p>
          )}
          {categories.isSuccess && categories.data.items.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategoryId(undefined)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-fast ${
                  !selectedCategoryId
                    ? 'border-accent-ring bg-accent-subtle text-accent-text'
                    : 'border-transparent bg-surface-2 text-fg-secondary hover:bg-surface-3'
                }`}
              >
                Vse
              </button>
              {categories.data.items.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategoryId(cat.id)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-fast ${
                    selectedCategoryId === cat.id
                      ? 'border-accent-ring bg-accent-subtle text-accent-text'
                      : 'border-transparent bg-surface-2 text-fg-secondary hover:bg-surface-3'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {viewingAsCompetitor && submissionProgress.isSuccess && submissionProgress.data.categories.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-1.5">
              <h2 className="font-semibold text-fg">Vaš napredek</h2>
              <InfoHint text="Vsaka kategorija ima svojo omejitev prispevkov na tekmovalca - napredek se šteje po kategorijah, ne skupaj za celotno tekmovanje." />
            </div>
          </CardHeader>
          <CardBody className="flex flex-col gap-3">
            {submissionProgress.data.categories.map((c) => (
              <div key={c.categoryId}>
                <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                  <span className="font-medium text-fg">{c.categoryName}</span>
                  <span className={`font-mono text-xs tabular-nums ${c.remaining === 0 ? 'text-success' : 'text-fg-muted'}`}>
                    {c.uploaded} / {c.max}
                    {c.remaining === 0 ? ' · zapolnjeno' : ` · še ${c.remaining}`}
                  </span>
                </div>
                <ProgressBar value={c.uploaded} max={c.max} />
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      {viewingAsCompetitor && <PaymentCard competitionId={competitionId} />}

      {viewingAsCompetitor && comp.status === CompetitionStatus.Active && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold text-fg">Oddaj prispevek</h2>
          </CardHeader>
          <CardBody>
            {submissionProgress.isSuccess && uploadableCategories.length === 0 && categories.data && categories.data.items.length > 0 ? (
              <EmptyState
                title="Dosegli ste omejitev v vseh kategorijah"
                description="Za to tekmovanje ste že oddali največje dovoljeno število prispevkov v vsaki kategoriji."
              />
            ) : (
              <SubmissionUploadForm
                competitionId={competitionId}
                categories={uploadableCategories}
                requirements={comp.imageRequirements}
              />
            )}
          </CardBody>
        </Card>
      )}

      {viewingAsCompetitor && comp.status === CompetitionStatus.Review && (
        <Card>
          <CardBody>
            <p className="text-sm font-medium text-fg">Oddaja prispevkov je zaključena</p>
            <p className="text-sm text-fg-muted">Organizator zdaj pregleduje prispevke. Prispevki, ki jih ne zavrnete, gredo v ocenjevanje.</p>
          </CardBody>
        </Card>
      )}

      {viewingAsStaff && canManageHere && COMPETITION_LIFECYCLE_ACTIONS[comp.status].length > 0 && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold text-fg">Stanje tekmovanja</h2>
          </CardHeader>
          <CardBody className="flex flex-col gap-3">
            <p className="text-sm text-fg-muted">
              {comp.status === CompetitionStatus.Draft && 'Tekmovanje je osnutek. Ko so nastavitve pripravljene, ga načrtujte.'}
              {comp.status === CompetitionStatus.Scheduled && 'Tekmovanje je objavljeno. Aktivirajte ga, da tekmovalci lahko oddajajo prispevke.'}
              {comp.status === CompetitionStatus.Active && 'Oddaja prispevkov je odprta. Ko se izteče rok, začnite pregled.'}
              {comp.status === CompetitionStatus.Review && 'Pregled prispevkov. Ko ste zavrnili neustrezne, začnite ocenjevanje.'}
              {comp.status === CompetitionStatus.Judging && 'Sodniki ocenjujejo. Ko končajo, zaključite ocenjevanje in izberite najboljše.'}
              {comp.status === CompetitionStatus.Awarding && 'Izberite prispevke za razstavo in podelite nagrade (stran Nagrade), nato objavite rezultate.'}
              {comp.status === CompetitionStatus.Completed && 'Rezultati so objavljeni.'}
            </p>
            <CompetitionLifecycleActions competitionId={competitionId} status={comp.status} highlightNext size="md" showError />
          </CardBody>
        </Card>
      )}

      {canModerate && comp.status <= CompetitionStatus.Judging && comp.status >= CompetitionStatus.Active && submissions.isSuccess && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold text-fg">Pregled prispevkov</h2>
          </CardHeader>
          <CardBody className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="UnderReview">Čaka na pregled: {pendingCount}</Badge>
              <Badge tone="Accepted">Sprejeto: {acceptedCount}</Badge>
              <Badge tone="Rejected">Zavrnjeno: {rejectedCount}</Badge>
              {pendingCount > 0 && (
                <Button type="button" size="sm" variant="secondary" onClick={() => setOnlyPending((v) => !v)}>
                  {onlyPending ? 'Prikaži vse' : 'Prikaži samo čakajoče'}
                </Button>
              )}
            </div>
            <p className="text-sm text-fg-muted">
              {comp.status === CompetitionStatus.Active &&
                'Oddaja je odprta. Ko jo zaprete (Začni pregled), zavrnite prispevke, ki ne ustrezajo pravilom.'}
              {comp.status === CompetitionStatus.Review &&
                'Zavrnite prispevke, ki ne ustrezajo pravilom. Vsi ostali bodo sprejeti, ko začnete ocenjevanje.'}
              {comp.status === CompetitionStatus.Judging &&
                'Ocenjevanje poteka. Za spremembo odločitev se vrnite na pregled.'}
            </p>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-semibold text-fg">{viewingAsCompetitor ? 'Moji prispevki' : 'Prispevki'}</h2>
            {selectionVisible && (
              <div className="flex gap-1" role="tablist" aria-label="Prispevki in izbor za razstavo">
                <Button type="button" size="sm" variant={onlySelected ? 'ghost' : 'secondary'} onClick={() => setOnlySelected(false)}>
                  Vsi prispevki
                </Button>
                <Button type="button" size="sm" variant={onlySelected ? 'secondary' : 'ghost'} onClick={() => setOnlySelected(true)}>
                  Izbrani za razstavo
                </Button>
              </div>
            )}
            {scoresAvailable && ranking.isSuccess && averageBySubmission.size > 0 && (
              <div className="flex gap-1" role="group" aria-label="Razvrščanje prispevkov">
                <Button type="button" size="sm" variant={sortByScore ? 'ghost' : 'secondary'} onClick={() => setSortByScore(false)}>
                  Privzeto
                </Button>
                <Button type="button" size="sm" variant={sortByScore ? 'secondary' : 'ghost'} onClick={() => setSortByScore(true)}>
                  Po oceni
                </Button>
              </div>
            )}
            {submissions.isSuccess && (submissions.data?.items.length ?? 0) > 1 && (
              <div className="w-full sm:w-72">
                <Input
                  type="search"
                  aria-label="Išči prispevke po imenu"
                  placeholder="Išči po imenu prispevka…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {submissions.isLoading && <PageSpinner />}
          {submissions.isError && (
            <ErrorState message={getApiErrorMessage(submissions.error)} onRetry={() => submissions.refetch()} />
          )}
          {submissions.isSuccess && visibleSubmissions.length === 0 &&
            (searchTerm ? (
              <EmptyState title="Ni zadetkov" description={`Noben prispevek ne vsebuje »${search.trim()}«.`} />
            ) : isJudgeHere && !canManageHere && viewingAsStaff && comp.status < CompetitionStatus.Judging ? (
              <EmptyState
                title="Prispevki še niso na voljo"
                description="Sodniki prispevke vidijo šele, ko se začne faza ocenjevanja."
              />
            ) : (
              <EmptyState title="Ni še prispevkov" />
            ))}
          {submissions.isSuccess && visibleSubmissions.length > 0 && (
            <div className="flex flex-col gap-6">
              {submissionGroups.map((group) => (
                <section key={group.id} className="flex flex-col gap-3">
                  {group.name && (
                    <h3 className="flex items-baseline gap-2 text-sm font-semibold text-fg">
                      {group.name}
                      <span className="font-mono text-xs font-normal text-fg-muted">{group.items.length}</span>
                    </h3>
                  )}
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {group.items.map((s) => (
                      <SubmissionCard
                        key={s.id}
                        submission={s}
                        averageScore={scoresAvailable ? averageBySubmission.get(s.id) : undefined}
                        canRename={
                          onlyMine && (s.status === SubmissionStatus.Draft || s.status === SubmissionStatus.Submitted)
                        }
                        canModerate={canModerate && reviewOpen}
                        selectedForExhibition={selectionVisible && selectedIds.has(s.id)}
                        onReject={() => {
                          rejectSubmission.reset();
                          setRejecting({ id: s.id, title: s.title });
                        }}
                        isRejecting={rejectSubmission.isPending && rejectSubmission.variables?.id === s.id}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
      </>
      )}
      {rejecting && (
        <RejectSubmissionModal
          title={rejecting.title}
          isLoading={rejectSubmission.isPending}
          error={rejectSubmission.isError ? getApiErrorMessage(rejectSubmission.error) : undefined}
          onClose={() => setRejecting(null)}
          onConfirm={(reason) =>
            rejectSubmission.mutate({ id: rejecting.id, reason }, { onSuccess: () => setRejecting(null) })
          }
        />
      )}
    </div>
  );
}
